import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Label2 } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, ShieldWarning, Camera, X, ArrowBendDownRight, TrendUp, TrendDown, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";

const TOKEN = () => localStorage.getItem("csp_token");
const thumbUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/media/${id}/thumbnail?auth=${TOKEN()}`;
const fullUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/media/${id}/download?auth=${TOKEN()}`;

const STATUS_CLS = {
  ok: "border-emerald-500 bg-emerald-500/10 text-emerald-600",
  mangel: "border-red-500 bg-red-500/10 text-red-600",
  entfaellt: "border-blue-500 bg-blue-500/10 text-blue-600",
  nicht_zugaenglich: "border-orange-500 bg-orange-500/10 text-orange-600",
  nicht_geprueft: "border-slate-400 bg-slate-400/10 text-slate-600 dark:text-slate-300",
};

export function PhotoStrip({ photos = [], assetId, onChange, readOnly }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    try {
      const ids = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); if (assetId) fd.append("asset_id", assetId); fd.append("category", "Wartung");
        const { data } = await api.post("/media/upload", fd);
        ids.push(data.id);
      }
      onChange([...(photos || []), ...ids]);
    } catch (e) { toast.error("Foto-Upload fehlgeschlagen", { description: formatApiError(e.response?.data?.detail) }); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {(photos || []).map((id) => (
        <a key={id} href={fullUrl(id)} target="_blank" rel="noreferrer" className="relative block h-14 w-14 overflow-hidden rounded-md border border-border" data-testid={`photo-${id}`}>
          <img src={thumbUrl(id)} alt="Foto" className="h-full w-full object-cover" />
          {!readOnly && <button type="button" onClick={(e) => { e.preventDefault(); onChange(photos.filter((p) => p !== id)); }} className="absolute right-0 top-0 bg-black/60 p-0.5 text-white"><X size={11} /></button>}
        </a>
      ))}
      {!readOnly && <>
        <input ref={ref} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => { upload(Array.from(e.target.files)); e.target.value = ""; }} />
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid="add-photo-btn"
          className="flex h-14 w-14 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-border text-[10px] text-muted-foreground hover:bg-accent">
          <Camera size={16} weight="duotone" /> {busy ? "…" : "Foto"}
        </button>
      </>}
    </div>
  );
}

// ---------------------------------------------------------------- Feldtyp-Registry (erweiterbar)
const StatusField = ({ field, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {(field.config?.options || []).map((o) => (
      <button key={o.key} type="button" data-testid={`${testid}-${o.key}`} onClick={() => onChange(o.key)}
        className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${value === o.key ? (STATUS_CLS[o.key] || "border-primary bg-primary/10 text-primary") : "border-border text-muted-foreground hover:bg-accent"}`}>{o.label}</button>
    ))}
  </div>
);

const MesswertField = ({ field, value, onChange, testid }) => {
  const c = field.config || {};
  const gw = c.grenzwert && typeof c.grenzwert === "object" ? [c.grenzwert.min != null && `min ${c.grenzwert.min}`, c.grenzwert.max != null && `max ${c.grenzwert.max}`].filter(Boolean).join("/") : c.grenzwert;
  const hint = [c.einheit && `Einheit ${c.einheit}`, c.sollbereich && `Soll ${c.sollbereich}`, gw && `Grenzwert ${gw}`, c.quelle && `Quelle: ${c.quelle}`].filter(Boolean).join(" · ");
  return (
    <div>
      <Input className="h-8 text-sm" data-testid={testid} inputMode="decimal" placeholder={c.einheit ? `Wert (${c.einheit})` : "Messwert"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      {hint && <div className="mt-0.5 text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
};

const TextField = ({ field, value, onChange, testid }) => {
  const t = { zahl: "number", datum: "date", zeit: "time" }[field.type] || "text";
  if (field.type === "text" && field.key !== "kommentar")
    return <Input className="h-8 text-sm" type={t} data-testid={testid} placeholder={field.label} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  return <Textarea className="min-h-[38px] text-sm" data-testid={testid} placeholder={field.label} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
};

const BooleanField = ({ value, onChange, testid }) => (
  <div className="flex gap-1.5">
    {[["ja", "Ja"], ["nein", "Nein"]].map(([k, l]) => (
      <button key={k} type="button" data-testid={`${testid}-${k}`} onClick={() => onChange(k)}
        className={`rounded-md border px-3 py-1 text-xs ${value === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{l}</button>
    ))}
  </div>
);

const SelectField = ({ field, value, onChange, testid }) => {
  const opts = (field.config?.options || []).map((o) => (typeof o === "string" ? { key: o, label: o } : o));
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-sm" data-testid={testid}><SelectValue placeholder={field.label} /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}{o.prioritaet ? ` · ${o.prioritaet}` : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
};

const MultiSelectField = ({ field, value = [], onChange, testid }) => {
  const opts = (field.config?.options || []).map((o) => (typeof o === "string" ? { key: o, label: o } : o));
  const toggle = (k) => { const s = new Set(value); s.has(k) ? s.delete(k) : s.add(k); onChange([...s]); };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {opts.map((o) => <button key={o.key} type="button" onClick={() => toggle(o.key)}
        className={`rounded-md border px-2 py-1 text-xs ${value.includes(o.key) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>{o.label}</button>)}
    </div>
  );
};

const FotoField = ({ value, onChange, ctx, testid }) => (
  <div data-testid={testid}><PhotoStrip photos={value || []} assetId={ctx?.assetId} onChange={onChange} /></div>
);

const StubField = ({ field, testid }) => (
  <div data-testid={testid} className="rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground">
    Feldtyp „{field.type}" — noch nicht erfasst (Architektur bereit, folgt in CMS-Ausbau).
  </div>
);

// type → Renderer. Neuer Feldtyp = ein Eintrag, keine Architekturänderung.
const FIELD_REGISTRY = {
  status: StatusField, messwert: MesswertField, text: TextField, zahl: TextField, datum: TextField, zeit: TextField,
  boolean: BooleanField, auswahl: SelectField, mehrfachauswahl: MultiSelectField, foto: FotoField,
  mangel_auswahl: SelectField, komponenten_auswahl: SelectField, ersatzteil_auswahl: SelectField,
  video: StubField, dokument: StubField, unterschrift: StubField, qrcode: StubField, ocr: StubField,
  ki_vorschlag: StubField, gps: StubField, sprache: StubField,
};

const isVisible = (field, values) => {
  const v = field.visible_if;
  if (!v) return true;
  return values?.[v.field] === v.eq;
};

// ---------------------------------------------------------------- Prüfpunkt-Karte
function CheckpointCard({ cp, values, onField, assetId }) {
  const answered = !!values?.befund;
  return (
    <div data-testid={`cms-cp-${cp.key}`} className={`rounded-md border p-3 ${answered ? "border-border" : "border-amber-300/60"}`}>
      <div className="flex items-start gap-2">
        {cp.sicherheitsrelevant && <ShieldWarning size={16} weight="fill" className="mt-0.5 shrink-0 text-red-500" />}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{cp.bezeichnung} {cp.pflicht && <span className="text-red-500">*</span>}</div>
          <div className="text-xs text-muted-foreground">{cp.norm || "ohne Norm"}{cp.code ? ` · ${cp.code}` : ""}</div>
          {cp.pruefanweisung && <div className="mt-0.5 text-xs text-muted-foreground italic">{cp.pruefanweisung}</div>}
        </div>
      </div>
      <div className="mt-2 space-y-2">
        {cp.fields.filter((f) => isVisible(f, values)).map((f) => {
          const Comp = FIELD_REGISTRY[f.type] || StubField;
          const tid = `cms-${cp.key}-${f.key}`;
          return (
            <div key={f.key}>
              {f.type !== "status" && <div className="mb-0.5"><Label2>{f.label}{f.pflicht ? " *" : ""}</Label2></div>}
              <Comp field={f} value={values?.[f.key]} onChange={(val) => onField(cp.key, f.key, val)} ctx={{ assetId }} testid={tid} />
            </div>
          );
        })}
      </div>
      {values?.befund === "mangel" && cp.folgepruefungen?.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-md bg-amber-500/5 p-2 text-xs text-amber-700 dark:text-amber-400" data-testid={`cms-followup-${cp.key}`}>
          <ArrowBendDownRight size={13} /> Folgeprüfungen: {cp.folgepruefungen.map((f) => f.bezeichnung).join(", ")}
        </div>
      )}
      {values?.befund === "mangel" && cp.reparatur_pakete?.length > 0 && (
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <Wrench size={13} /> Reparaturpakete: {cp.reparatur_pakete.map((r) => r.name || r.key).join(", ")}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Renderer (Wartung)
export function CmsAssetProtocol({ jobId, assetId, onDone }) {
  const [proto, setProto] = useState(null);
  const [res, setRes] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/v1/cms/protocol/${assetId}`).then((r) => setProto(r.data)); }, [assetId]);
  const onField = (cpKey, fieldKey, val) => setRes((p) => ({ ...p, [cpKey]: { ...(p[cpKey] || {}), [fieldKey]: val } }));

  if (!proto) return <div className="p-4"><Skeleton className="h-40 rounded-md" /></div>;
  if (!proto.has_catalog)
    return <div className="bg-muted/20 px-4 py-4"><div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground" data-testid="cms-no-catalog">
      Für den Anlagentyp „{proto.asset?.asset_type_key || proto.asset?.asset_type || "unbekannt"}" ist kein CMS-Katalog vorhanden. Bitte im CMS-Modul einen Katalog importieren.</div></div>;

  const answered = proto.gruppen.reduce((n, g) => n + g.checkpoints.filter((cp) => res[cp.key]?.befund).length, 0);

  const complete = async () => {
    const results = [];
    proto.gruppen.forEach((g) => g.checkpoints.forEach((cp) => {
      const v = res[cp.key];
      if (v && Object.keys(v).length) results.push({ checkpoint_key: cp.key, group_key: g.group_key, values: v });
    }));
    setSaving(true);
    try {
      const { data } = await api.post(`/v1/cms/protocol/${assetId}/complete`, { job_id: jobId, results, bemerkung: note || null });
      const m = (data.mangel || 0) + (data.sicherheitsmangel || 0);
      toast.success("Anlage abgeschlossen", { description: `Health ${data.health_before}% → ${data.health_after}% · ${m} Mangel/Mängel · ${data.unpruef || 0} ungeprüft` });
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = d && Array.isArray(d.errors) ? d.errors.join(" · ") : formatApiError(d);
      toast.error("Abschluss nicht möglich", { description: msg });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-muted/20 px-4 py-4" data-testid={`cms-protocol-${assetId}`}>
      <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>Generisch aus CMS-Katalog <b>{proto.catalog?.key}</b> ({proto.catalog?.name}) gerendert</span>
        <span className="font-semibold">{answered}/{proto.total_checkpoints} bewertet · {proto.mandatory_count} Pflicht</span>
      </div>
      <div className="space-y-4">
        {proto.gruppen.map((g, gi) => (
          <div key={g.group_key} data-testid={`cms-group-${g.group_key}`}>
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded bg-primary/10 text-[11px] font-bold text-primary">{gi + 1}</span>
              <Label2>{g.group_name}</Label2><span className="text-xs text-muted-foreground">({g.checkpoints.length})</span>
            </div>
            <div className="space-y-2">
              {g.checkpoints.map((cp) => <CheckpointCard key={cp.key} cp={cp} values={res[cp.key]} onField={onField} assetId={assetId} />)}
            </div>
          </div>
        ))}
        <div><Label2>Gesamt-Bemerkung (optional)</Label2><Textarea data-testid="cms-protocol-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Auffälligkeiten, Hinweise …" /></div>
        <Button className="w-full gap-2" data-testid={`cms-complete-${assetId}`} disabled={saving} onClick={complete}><CheckCircle size={16} weight="bold" /> Anlage abschließen</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Doku (read-only)
export function CmsProtocolDocumentation({ jobId, assetId }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.get(`/v1/cms/protocol/${assetId}/documentation?job_id=${jobId}`).then((r) => setDoc(r.data)); }, [jobId, assetId]);
  if (!doc) return <div className="p-4"><Skeleton className="h-40 rounded-md" /></div>;
  const m = doc.maintenance;
  const HealthIcon = (m.health_after ?? 0) >= (m.health_before ?? 0) ? TrendUp : TrendDown;
  const meta = [
    ["Kunde", doc.customer?.name], ["Standort", doc.site?.name], ["Anlage", doc.asset?.name],
    ["Anlagentyp", doc.asset?.asset_type], ["Katalog", doc.catalog ? `${doc.catalog.key} v${doc.catalog.version}` : "—"],
    ["Monteur", m.completed_by || m.monteur_name], ["Datum", m.completed_at ? new Date(m.completed_at).toLocaleString("de-DE") : "—"],
  ];
  return (
    <div className="bg-muted/20 px-4 py-4" data-testid={`cms-documentation-${assetId}`}>
      <div className="mb-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
        {meta.map(([k, v]) => (<div key={k}><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{k}</div><div className="text-sm font-medium">{v || "—"}</div></div>))}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
        <Badge variant="outline" className="rounded-md border-red-500/40 bg-red-500/10 text-red-600">Mängel: {m.mangel_count || 0}</Badge>
        <Badge variant="outline" className="rounded-md border-red-600/50 bg-red-600/10 text-red-700">Sicherheitsmängel: {m.sicherheitsmangel_count || 0}</Badge>
        <Badge variant="outline" className="rounded-md border-orange-500/40 bg-orange-500/10 text-orange-600">Ungeprüft: {m.unpruef_count || 0}</Badge>
        <Badge variant="outline" className="rounded-md border-blue-500/40 bg-blue-500/10 text-blue-600">Entfällt: {m.entfaellt_count || 0}</Badge>
        {m.health_before != null && <Badge variant="outline" className="gap-1 rounded-md"><HealthIcon size={12} weight="bold" /> Health {m.health_before}% → {m.health_after}%</Badge>}
      </div>
      {doc.empfohlene_massnahmen?.length > 0 && (
        <div className="mb-4 rounded-md border border-amber-300/60 bg-amber-500/5 p-3" data-testid="cms-doc-massnahmen">
          <Label2>Festgestellte Mängel & empfohlene Maßnahmen</Label2>
          <div className="mt-2 space-y-1.5">
            {doc.empfohlene_massnahmen.map((x, i) => (
              <div key={i} className="text-sm">
                {x.sicherheitsrelevant && <ShieldWarning size={13} weight="fill" className="mr-1 inline text-red-500" />}
                <span className="font-medium">{x.beschreibung}</span>
                {x.mangel && <span className="text-muted-foreground"> — {x.mangel}</span>}
                {x.massnahme && <span className="text-amber-700 dark:text-amber-400"> → {x.massnahme}</span>}
                {x.folgepruefungen?.length > 0 && <span className="text-xs text-muted-foreground"> · Folge: {x.folgepruefungen.map((f) => f.bezeichnung).join(", ")}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-4">
        {doc.sections.map((s) => (
          <div key={s.title} data-testid={`cms-doc-section-${s.title}`}>
            <div className="mb-2"><Label2>{s.title}</Label2></div>
            <div className="divide-y divide-border rounded-md border border-border">
              {s.checkpoints.map((cp, i) => (
                <div key={i} className="flex flex-col gap-1 px-3 py-2 text-sm sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {cp.sicherheitsrelevant && <ShieldWarning size={13} weight="fill" className="shrink-0 text-red-500" />}
                      <span className="font-medium">{cp.bezeichnung}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">{cp.norm || "ohne Norm"}{cp.code ? ` · ${cp.code}` : ""}
                      {cp.messwert != null && cp.messwert !== "" && <span> · Messwert: <span className="font-semibold text-foreground">{cp.messwert}</span></span>}
                      {cp.bemerkung && <span> · {cp.bemerkung}</span>}
                      {cp.massnahme && <span className="text-amber-700 dark:text-amber-400"> → {cp.massnahme}</span>}
                    </div>
                    {cp.fotos?.length > 0 && <PhotoStrip photos={cp.fotos} readOnly />}
                  </div>
                  <Badge variant="outline" className={`shrink-0 rounded-md text-xs ${STATUS_CLS[cp.befund] || "text-muted-foreground"}`}>{cp.befund_label || "—"}</Badge>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {m.bemerkung && <div className="mt-4 rounded-md border border-border p-3 text-sm"><Label2>Abschluss-Bemerkung</Label2><div className="mt-1">{m.bemerkung}</div></div>}
    </div>
  );
}
